/**
 * Product Pricing Catalog — Pantalla B · Editor de Producto.
 * 5 secciones: Info General · Tipos · Cantidades · Extras · Matriz Costo/Precio.
 * Sprint P1. Sólo edita el producto seleccionado. No integra con Estimates.
 */
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { productCatalogApi } from "./api";
import { API, useAuth } from "@/context/AuthContext";

function Section({ title, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="border-2 border-black bg-white p-4 space-y-3"
    >
      <div className="label-overline text-[var(--text-secondary)]">{title}</div>
      {children}
    </section>
  );
}

export default function ProductCatalogEditorPage({ productId, onBack }) {
  const { isAdmin } = useAuth();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState([]);
  const [matrixDirty, setMatrixDirty] = useState({}); // key `${type_id}|${quantity_id}` -> {cost, price}
  // Sprint P2 · BUG 4 (fix definitivo) — `savedProduct` refleja el último
  // estado confirmado por el backend. Sólo sus ids son "definitivos".
  const [savedProduct, setSavedProduct] = useState(null);

  const reload = async () => {
    setLoading(true);
    try {
      const [p, m] = await Promise.all([
        productCatalogApi.get(productId),
        productCatalogApi.getMatrix(productId),
      ]);
      setProduct(p);
      setSavedProduct(p);
      setMatrix(m);
      setMatrixDirty({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (productId) reload();
  }, [productId]);

  // ---------- General ----------
  const saveGeneral = async () => {
    try {
      const updated = await productCatalogApi.update(productId, {
        name: product.name,
        category: product.category,
        photo_url: product.photo_url,
        active: product.active,
      });
      setProduct((cur) => ({ ...cur, ...updated }));
      toast.success("Información general guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // ---------- Imágenes (galería · micro-sprint) ----------
  const [uploadingImg, setUploadingImg] = useState(false);
  const imgSrc = (imageId) =>
    `${API}/product-catalog/products/${productId}/images/${imageId}`;
  const applyProduct = (data) => {
    setProduct((cur) => ({ ...cur, ...data }));
    setSavedProduct((cur) => ({ ...cur, ...data }));
  };
  const uploadImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploadingImg(true);
    try {
      const { data } = await axios.post(
        `${API}/product-catalog/products/${productId}/images`,
        fd
      );
      applyProduct(data);
      toast.success("Imagen subida");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al subir imagen");
    } finally {
      setUploadingImg(false);
    }
  };
  const setMainImage = async (imageId) => {
    try {
      const { data } = await axios.put(
        `${API}/product-catalog/products/${productId}/images/${imageId}/main`
      );
      applyProduct(data);
      toast.success("Imagen principal actualizada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const deleteImage = async (imageId) => {
    try {
      const { data } = await axios.delete(
        `${API}/product-catalog/products/${productId}/images/${imageId}`
      );
      applyProduct(data);
      toast.success("Imagen eliminada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // ---------- IDs definitivos (Sprint P2 · BUG 4, fix definitivo) ----------
  // Un id es definitivo sólo si el backend lo devolvió en la última carga o
  // guardado. Las filas nuevas (id local `new-*`) se envían SIN id para que
  // el backend genere el uuid definitivo; la matriz sólo acepta ids
  // confirmados por el backend.
  const savedTypeIds = useMemo(
    () => new Set((savedProduct?.types || []).map((t) => t.id)),
    [savedProduct]
  );
  const savedQtyIds = useMemo(
    () => new Set((savedProduct?.quantities || []).map((q) => q.id)),
    [savedProduct]
  );

  // ---------- Types ----------
  const saveTypes = async (nextTypes) => {
    try {
      const payload = nextTypes.map((t) =>
        savedTypeIds.has(t.id) ? t : { ...t, id: null }
      );
      const updated = await productCatalogApi.replaceTypes(productId, payload);
      setProduct(updated);
      setSavedProduct(updated);
      // matrix might have lost cells → reload matrix
      const m = await productCatalogApi.getMatrix(productId);
      setMatrix(m);
      toast.success("Tipos guardados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const addType = () => {
    setProduct((cur) => ({
      ...cur,
      types: [
        ...(cur.types || []),
        { id: `new-${Date.now()}`, name: "", sort_order: (cur.types || []).length, active: true },
      ],
    }));
  };

  // ---------- Quantities ----------
  const saveQuantities = async (nextQuantities) => {
    try {
      const payload = nextQuantities.map((q) =>
        savedQtyIds.has(q.id) ? q : { ...q, id: null }
      );
      const updated = await productCatalogApi.replaceQuantities(productId, payload);
      setProduct(updated);
      setSavedProduct(updated);
      const m = await productCatalogApi.getMatrix(productId);
      setMatrix(m);
      toast.success("Cantidades guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const addQuantity = () => {
    setProduct((cur) => ({
      ...cur,
      quantities: [
        ...(cur.quantities || []),
        { id: `new-${Date.now()}`, quantity: 0, max_qty: null, sort_order: (cur.quantities || []).length, active: true },
      ],
    }));
  };
  const setQuantityMode = async (mode) => {
    try {
      const updated = await productCatalogApi.update(productId, { quantity_mode: mode });
      setProduct((cur) => ({ ...cur, ...updated }));
      setSavedProduct((cur) => ({ ...cur, ...updated }));
      toast.success(mode === "range" ? "Modo rangos activado" : "Modo cantidades exactas activado");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // ---------- Extras ----------
  const saveExtras = async (nextExtras) => {
    try {
      const updated = await productCatalogApi.replaceExtras(productId, nextExtras);
      setProduct(updated);
      toast.success("Extras guardados");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };
  const addExtra = () => {
    setProduct((cur) => ({
      ...cur,
      extras: [
        ...(cur.extras || []),
        {
          id: `new-${Date.now()}`,
          name: "",
          cost: 0,
          price: 0,
          charge_mode: "flat",
          sort_order: (cur.extras || []).length,
          active: true,
        },
      ],
    }));
  };

  // ---------- Matrix ----------
  const matrixIndex = useMemo(() => {
    const idx = {};
    for (const c of matrix) idx[`${c.type_id}|${c.quantity_id}`] = c;
    return idx;
  }, [matrix]);

  const setCellField = (type_id, quantity_id, field, value) => {
    const key = `${type_id}|${quantity_id}`;
    const current = matrixDirty[key] || matrixIndex[key] || { cost: 0, price: 0 };
    setMatrixDirty((cur) => ({
      ...cur,
      [key]: { ...current, [field]: value === "" ? 0 : Number(value) },
    }));
  };
  const getCellValue = (type_id, quantity_id, field) => {
    const key = `${type_id}|${quantity_id}`;
    if (matrixDirty[key]) return matrixDirty[key][field] ?? 0;
    return matrixIndex[key]?.[field] ?? 0;
  };
  const saveMatrix = async () => {
    const allEntries = Object.entries(matrixDirty);
    const entries = allEntries.filter(([key]) => {
      const [type_id, quantity_id] = key.split("|");
      return savedTypeIds.has(type_id) && savedQtyIds.has(quantity_id);
    });
    if (entries.length < allEntries.length) {
      toast.error("Guardá primero los tipos y cantidades nuevos antes de editar su matriz.");
    }
    const cells = entries.map(([key, v]) => {
      const [type_id, quantity_id] = key.split("|");
      return { type_id, quantity_id, cost: Number(v.cost || 0), price: Number(v.price || 0) };
    });
    if (cells.length === 0) {
      toast.info("Sin cambios en la matriz");
      return;
    }
    try {
      const m = await productCatalogApi.saveMatrix(productId, cells);
      setMatrix(m);
      setMatrixDirty({});
      toast.success("Matriz guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  if (loading || !product) {
    return (
      <div className="font-mono-tech text-sm text-[var(--text-secondary)] py-8 text-center">
        LOADING…
      </div>
    );
  }

  const types = product.types || [];
  const quantities = product.quantities || [];
  const extras = product.extras || [];

  return (
    <div data-testid="product-catalog-editor" className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <button
            data-testid="pc-editor-back"
            onClick={onBack}
            className="text-sm underline text-[var(--text-secondary)] hover:text-black"
          >
            ← Volver al catálogo
          </button>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">
            {product.name}
          </h2>
        </div>
        {!isAdmin && (
          <span
            data-testid="pc-editor-readonly-badge"
            className="font-mono-tech text-[10px] font-bold tracking-widest px-2 py-1 bg-[#EEF2FF] border border-[#0E5FFF] text-[#0E5FFF]"
          >
            SOLO LECTURA
          </span>
        )}
      </div>

      <fieldset
        disabled={!isAdmin}
        data-testid="pc-editor-fieldset"
        className={`border-0 p-0 m-0 space-y-4 ${!isAdmin ? "opacity-95" : ""}`}
      >

      {/* --- 1. Información General --- */}
      <Section title="INFORMACIÓN GENERAL" testId="pc-section-general">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label-overline block mb-1">Nombre</label>
            <input
              data-testid="pc-general-name"
              value={product.name}
              onChange={(e) => setProduct({ ...product, name: e.target.value })}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
            />
          </div>
          <div>
            <label className="label-overline block mb-1">Categoría</label>
            <input
              data-testid="pc-general-category"
              value={product.category || ""}
              onChange={(e) => setProduct({ ...product, category: e.target.value })}
              className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="label-overline block mb-1">URL de foto</label>
            <input
              data-testid="pc-general-photo"
              value={product.photo_url || ""}
              onChange={(e) => setProduct({ ...product, photo_url: e.target.value })}
              placeholder="https://..."
              className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
            />
            {product.photo_url && (
              <img
                src={product.photo_url}
                alt="preview"
                className="mt-2 max-w-[160px] max-h-[120px] object-cover border border-[var(--border)]"
              />
            )}
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              data-testid="pc-general-active"
              type="checkbox"
              checked={!!product.active}
              onChange={(e) => setProduct({ ...product, active: e.target.checked })}
            />
            <span className="font-display font-bold">Activo</span>
          </label>
        </div>
        <div className={isAdmin ? "flex justify-end" : "hidden"}>
          <button
            data-testid="pc-general-save"
            onClick={saveGeneral}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm"
          >
            Guardar información general
          </button>
        </div>
      </Section>

      {/* --- 1b. Imágenes del producto (galería) --- */}
      <Section
        title={`IMÁGENES (${(product.images || []).length})`}
        testId="pc-section-images"
      >
        {isAdmin && (
          <div>
            <label
              data-testid="pc-image-upload-label"
              className={`inline-block bg-black text-white px-4 py-2 font-display font-bold text-sm cursor-pointer ${
                uploadingImg ? "opacity-50 pointer-events-none" : ""
              }`}
            >
              {uploadingImg ? "Subiendo…" : "+ Subir imagen"}
              <input
                data-testid="pc-image-upload-input"
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                className="hidden"
                onChange={(e) => {
                  uploadImage(e.target.files?.[0]);
                  e.target.value = "";
                }}
              />
            </label>
            <span className="ml-3 text-xs text-[var(--text-secondary)]">
              PNG, JPG, JPEG o WEBP · máx 10MB
            </span>
          </div>
        )}
        {(product.images || []).length === 0 ? (
          <div
            data-testid="pc-images-empty"
            className="text-sm italic text-[var(--text-secondary)]"
          >
            Sin imágenes subidas.
          </div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {(product.images || []).map((img) => (
              <div
                key={img.id}
                data-testid={`pc-image-card-${img.id}`}
                className={`border-2 p-2 w-[150px] space-y-1 ${
                  img.is_main ? "border-black" : "border-[var(--border)]"
                }`}
              >
                <div className="relative">
                  <img
                    src={imgSrc(img.id)}
                    alt={img.name}
                    className="w-full h-[96px] object-cover border border-[var(--border)]"
                  />
                  {img.is_main && (
                    <span
                      data-testid={`pc-image-main-badge-${img.id}`}
                      className="absolute top-1 left-1 font-mono-tech text-[9px] font-bold tracking-widest bg-black text-white px-1.5 py-0.5"
                    >
                      PRINCIPAL
                    </span>
                  )}
                </div>
                <div className="font-mono-tech text-[10px] truncate" title={img.name}>
                  {img.name}
                </div>
                {isAdmin && (
                  <div className="flex gap-1">
                    {!img.is_main && (
                      <button
                        type="button"
                        data-testid={`pc-image-set-main-${img.id}`}
                        onClick={() => setMainImage(img.id)}
                        className="flex-1 font-mono-tech text-[10px] font-bold border border-black px-1 py-1 hover:bg-black hover:text-white transition-colors"
                      >
                        Principal
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid={`pc-image-delete-${img.id}`}
                      onClick={() => deleteImage(img.id)}
                      className="flex-1 font-mono-tech text-[10px] font-bold border border-red-600 text-red-600 px-1 py-1 hover:bg-red-600 hover:text-white transition-colors"
                    >
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* --- 2. Tipos --- */}
      <Section title="TIPOS" testId="pc-section-types">
        <div className="space-y-2">
          {types.length === 0 && (
            <div className="text-sm italic text-[var(--text-secondary)]">
              Aún no hay tipos. Agregá al menos uno.
            </div>
          )}
          {types.map((t, i) => (
            <div key={t.id} className="grid grid-cols-12 gap-2 items-center">
              <input
                data-testid={`pc-type-name-${i}`}
                value={t.name}
                placeholder="Ej. Standard 14 pt"
                onChange={(e) => {
                  const next = [...types];
                  next[i] = { ...t, name: e.target.value };
                  setProduct({ ...product, types: next });
                }}
                className="col-span-6 border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
              />
              <input
                data-testid={`pc-type-order-${i}`}
                type="number"
                value={t.sort_order}
                onChange={(e) => {
                  const next = [...types];
                  next[i] = { ...t, sort_order: Number(e.target.value) };
                  setProduct({ ...product, types: next });
                }}
                className="col-span-2 border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
              />
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  data-testid={`pc-type-active-${i}`}
                  type="checkbox"
                  checked={!!t.active}
                  onChange={(e) => {
                    const next = [...types];
                    next[i] = { ...t, active: e.target.checked };
                    setProduct({ ...product, types: next });
                  }}
                />
                Activo
              </label>
              <button
                data-testid={`pc-type-del-${i}`}
                onClick={() => {
                  const next = types.filter((_, j) => j !== i);
                  setProduct({ ...product, types: next });
                }}
                className={`col-span-2 border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-2 py-1.5 text-xs hover:bg-[var(--alert-overdue)] hover:text-white ${!isAdmin ? "hidden" : ""}`}
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
        <div className={isAdmin ? "flex justify-between" : "hidden"}>
          <button
            data-testid="pc-type-add"
            onClick={addType}
            className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
          >
            + Añadir tipo
          </button>
          <button
            data-testid="pc-type-save"
            onClick={() => saveTypes(types)}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm"
          >
            Guardar tipos
          </button>
        </div>
      </Section>

      {/* --- 3. Cantidades --- */}
      <Section title="CANTIDADES" testId="pc-section-quantities">
        <div className={isAdmin ? "flex items-center gap-2 mb-3" : "hidden"}>
          <span className="label-overline">Modo:</span>
          <button
            data-testid="pc-qty-mode-package"
            onClick={() => setQuantityMode("package")}
            className={`px-3 py-1.5 text-xs font-mono-tech border ${
              (product.quantity_mode || "package") === "package"
                ? "bg-black text-white border-black"
                : "border-[var(--border)] hover:bg-[var(--surface-alt)]"
            }`}
          >
            Cantidades exactas (precio por lote)
          </button>
          <button
            data-testid="pc-qty-mode-range"
            onClick={() => setQuantityMode("range")}
            className={`px-3 py-1.5 text-xs font-mono-tech border ${
              product.quantity_mode === "range"
                ? "bg-black text-white border-black"
                : "border-[var(--border)] hover:bg-[var(--surface-alt)]"
            }`}
          >
            Rangos (precio por unidad)
          </button>
        </div>
        {product.quantity_mode === "range" && (
          <div className="text-xs text-[var(--text-secondary)] mb-3">
            Cada fila define un rango: mínimo y máximo (dejá el máximo vacío en el último rango para
            representar &quot;72+&quot;). El precio de la matriz será el <b>precio por unidad</b> de ese rango.
          </div>
        )}
        <div className="space-y-2">
          {quantities.length === 0 && (
            <div className="text-sm italic text-[var(--text-secondary)]">
              Aún no hay cantidades.
            </div>
          )}
          {quantities.map((q, i) => (
            <div key={q.id} className="grid grid-cols-12 gap-2 items-center">
              <input
                data-testid={`pc-qty-value-${i}`}
                type="number"
                min="1"
                value={q.quantity}
                placeholder={product.quantity_mode === "range" ? "Desde" : "Cantidad"}
                onChange={(e) => {
                  const next = [...quantities];
                  next[i] = { ...q, quantity: Number(e.target.value) };
                  setProduct({ ...product, quantities: next });
                }}
                className={`${product.quantity_mode === "range" ? "col-span-3" : "col-span-6"} border border-[var(--border)] px-3 py-2 text-sm font-mono-tech`}
              />
              {product.quantity_mode === "range" && (
                <input
                  data-testid={`pc-qty-max-${i}`}
                  type="number"
                  min="1"
                  value={q.max_qty ?? ""}
                  placeholder="Hasta (vacío = ∞)"
                  onChange={(e) => {
                    const next = [...quantities];
                    next[i] = { ...q, max_qty: e.target.value === "" ? null : Number(e.target.value) };
                    setProduct({ ...product, quantities: next });
                  }}
                  className="col-span-3 border border-[var(--border)] px-3 py-2 text-sm font-mono-tech"
                />
              )}
              <input
                data-testid={`pc-qty-order-${i}`}
                type="number"
                value={q.sort_order}
                onChange={(e) => {
                  const next = [...quantities];
                  next[i] = { ...q, sort_order: Number(e.target.value) };
                  setProduct({ ...product, quantities: next });
                }}
                className={`${product.quantity_mode === "range" ? "col-span-1" : "col-span-2"} border border-[var(--border)] px-3 py-2 text-sm font-mono-tech`}
              />
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  data-testid={`pc-qty-active-${i}`}
                  type="checkbox"
                  checked={!!q.active}
                  onChange={(e) => {
                    const next = [...quantities];
                    next[i] = { ...q, active: e.target.checked };
                    setProduct({ ...product, quantities: next });
                  }}
                />
                Activo
              </label>
              <button
                data-testid={`pc-qty-del-${i}`}
                onClick={() => {
                  const next = quantities.filter((_, j) => j !== i);
                  setProduct({ ...product, quantities: next });
                }}
                className={`col-span-2 border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-2 py-1.5 text-xs hover:bg-[var(--alert-overdue)] hover:text-white ${!isAdmin ? "hidden" : ""}`}
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
        <div className={isAdmin ? "flex justify-between" : "hidden"}>
          <button
            data-testid="pc-qty-add"
            onClick={addQuantity}
            className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
          >
            + Añadir cantidad
          </button>
          <button
            data-testid="pc-qty-save"
            onClick={() => saveQuantities(quantities)}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm"
          >
            Guardar cantidades
          </button>
        </div>
      </Section>

      {/* --- 4. Extras --- */}
      <Section title="EXTRAS" testId="pc-section-extras">
        <div className="space-y-2">
          {extras.length === 0 && (
            <div className="text-sm italic text-[var(--text-secondary)]">
              Aún no hay extras.
            </div>
          )}
          {extras.map((ex, i) => (
            <div key={ex.id} className="grid grid-cols-12 gap-2 items-center">
              <input
                data-testid={`pc-extra-name-${i}`}
                value={ex.name}
                placeholder="Ej. Back Side"
                onChange={(e) => {
                  const next = [...extras];
                  next[i] = { ...ex, name: e.target.value };
                  setProduct({ ...product, extras: next });
                }}
                className="col-span-3 border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-black"
              />
              <div className={isAdmin ? "col-span-2" : "hidden"}>
                <label className="label-overline block text-[9px]">Costo</label>
                <input
                  data-testid={`pc-extra-cost-${i}`}
                  type="number"
                  step="0.01"
                  value={ex.cost}
                  onChange={(e) => {
                    const next = [...extras];
                    next[i] = { ...ex, cost: Number(e.target.value) };
                    setProduct({ ...product, extras: next });
                  }}
                  className="w-full border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech"
                />
              </div>
              <div className="col-span-2">
                <label className="label-overline block text-[9px]">Precio</label>
                <input
                  data-testid={`pc-extra-price-${i}`}
                  type="number"
                  step="0.01"
                  value={ex.price}
                  onChange={(e) => {
                    const next = [...extras];
                    next[i] = { ...ex, price: Number(e.target.value) };
                    setProduct({ ...product, extras: next });
                  }}
                  className="w-full border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech"
                />
              </div>
              <div className="col-span-2">
                <label className="label-overline block text-[9px]">Cobro</label>
                <select
                  data-testid={`pc-extra-mode-${i}`}
                  value={ex.charge_mode || "flat"}
                  onChange={(e) => {
                    const next = [...extras];
                    next[i] = { ...ex, charge_mode: e.target.value };
                    setProduct({ ...product, extras: next });
                  }}
                  className="w-full border border-[var(--border)] px-2 py-1.5 text-sm font-mono-tech bg-white"
                >
                  <option value="flat">Cargo único</option>
                  <option value="per_unit">Por unidad</option>
                </select>
              </div>
              <label className="col-span-1 flex items-center gap-1 text-xs">
                <input
                  data-testid={`pc-extra-active-${i}`}
                  type="checkbox"
                  checked={!!ex.active}
                  onChange={(e) => {
                    const next = [...extras];
                    next[i] = { ...ex, active: e.target.checked };
                    setProduct({ ...product, extras: next });
                  }}
                />
                Activo
              </label>
              <button
                data-testid={`pc-extra-del-${i}`}
                onClick={() => {
                  const next = extras.filter((_, j) => j !== i);
                  setProduct({ ...product, extras: next });
                }}
                className={`col-span-2 border border-[var(--alert-overdue)] text-[var(--alert-overdue)] px-2 py-1.5 text-xs hover:bg-[var(--alert-overdue)] hover:text-white ${!isAdmin ? "hidden" : ""}`}
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
        <div className={isAdmin ? "flex justify-between" : "hidden"}>
          <button
            data-testid="pc-extra-add"
            onClick={addExtra}
            className="border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-[var(--surface-alt)]"
          >
            + Añadir extra
          </button>
          <button
            data-testid="pc-extra-save"
            onClick={() => saveExtras(extras)}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm"
          >
            Guardar extras
          </button>
        </div>
      </Section>

      {/* --- 5. Matriz Costo / Precio --- */}
      <Section title="MATRIZ COSTO / PRECIO (TIPO × CANTIDAD)" testId="pc-section-matrix">
        {types.length === 0 || quantities.length === 0 ? (
          <div className="text-sm italic text-[var(--text-secondary)]">
            Para editar la matriz necesitás guardar al menos un tipo y una cantidad.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table data-testid="pc-matrix-table" className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border border-[var(--border)] bg-[var(--surface-alt)] px-2 py-2 text-left label-overline">
                    Tipo \ Cantidad
                  </th>
                  {quantities.map((q) => (
                    <th
                      key={q.id}
                      className="border border-[var(--border)] bg-[var(--surface-alt)] px-2 py-2 label-overline whitespace-nowrap"
                    >
                      {product.quantity_mode === "range"
                        ? `${q.quantity}${q.max_qty ? `–${q.max_qty}` : "+"} (c/u)`
                        : q.quantity}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id}>
                    <th className="border border-[var(--border)] px-2 py-2 text-left font-display font-bold whitespace-nowrap">
                      {t.name}
                    </th>
                    {quantities.map((q) => {
                      const key = `${t.id}|${q.id}`;
                      const dirty = !!matrixDirty[key];
                      const pending = !savedTypeIds.has(t.id) || !savedQtyIds.has(q.id);
                      if (pending) {
                        return (
                          <td
                            key={q.id}
                            data-testid={`pc-cell-pending-${t.id}-${q.id}`}
                            className="border border-[var(--border)] px-2 py-2 align-middle text-center"
                          >
                            <span className="font-mono-tech text-[10px] text-[var(--text-secondary)] italic">
                              Guardá primero
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td
                          key={q.id}
                          className={`border border-[var(--border)] px-1 py-1 align-top ${
                            dirty ? "bg-amber-50" : ""
                          }`}
                        >
                          <div className="flex flex-col gap-1">
                            {isAdmin && (
                              <div className="flex items-center gap-1">
                                <span className="label-overline text-[9px]">C</span>
                                <input
                                  data-testid={`pc-cell-cost-${t.id}-${q.id}`}
                                  type="number"
                                  step="0.01"
                                  value={getCellValue(t.id, q.id, "cost")}
                                  onChange={(e) => setCellField(t.id, q.id, "cost", e.target.value)}
                                  className="w-24 border border-[var(--border)] px-1.5 py-1 text-xs font-mono-tech"
                                />
                              </div>
                            )}
                            <div className="flex items-center gap-1">
                              <span className="label-overline text-[9px]">P</span>
                              <input
                                data-testid={`pc-cell-price-${t.id}-${q.id}`}
                                type="number"
                                step="0.01"
                                value={getCellValue(t.id, q.id, "price")}
                                onChange={(e) => setCellField(t.id, q.id, "price", e.target.value)}
                                className="w-24 border border-[var(--border)] px-1.5 py-1 text-xs font-mono-tech"
                              />
                            </div>
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className={isAdmin ? "flex justify-end" : "hidden"}>
          <button
            data-testid="pc-matrix-save"
            onClick={saveMatrix}
            disabled={Object.keys(matrixDirty).length === 0}
            className="bg-black text-white px-4 py-2 font-display font-bold text-sm disabled:opacity-50"
          >
            Guardar matriz
          </button>
        </div>
      </Section>
      </fieldset>
    </div>
  );
}
